import React, { useState } from "react";
import { FaChevronDown, FaChevronRight, FaEdit, FaRegTrashAlt } from "react-icons/fa";
import HeaderButtons from "components/Button/HeaderButtons";
import {
  Feedback,
  formatFeedbackDateTime,
  getQuarterMonths,
} from "services/Feedback/feedbackInterface";
import {
  FeedbackKindTag,
  FeedbackStatusBadge,
  FeedbackVisibilityTag,
  RatingDisplay,
} from "./FeedbackBits";

/**
 * One feedback record, read-only.
 *
 * Used for both kinds: a SELF goal sheet (with its rows) and MANAGER feedback
 * (usually just a comment). The goal rows collapse, because a received list can
 * hold several records and expanded tables would bury the comments.
 *
 * NOTE: the full comment text is always shown, never truncated. In a list a
 * 30-character preview is defensible; here the prose IS the content.
 */
export interface FeedbackCardProps {
  feedback: Feedback;
  /** Shown when the viewer may act on it; the caller decides from `editable`. */
  onEdit?: (feedback: Feedback) => void;
  onDelete?: (feedback: Feedback) => void;
  /** Approve is offered only on a SUBMITTED self sheet the viewer may review. */
  onApprove?: (feedback: Feedback) => void;
  /** Opens the linked record (a sheet's approval, or an approval's sheet). */
  onOpenLinked?: (feedbackId: number) => void;
  /**
   * CHANGE: share / hide MANAGER feedback. Passed only when this viewer may do
   * it (the author or an admin); the card itself never works that out.
   */
  onToggleShare?: (feedback: Feedback, next: boolean) => void;
  /** True while a share toggle is in flight, so the control can be disabled. */
  sharing?: boolean;
  /**
   * CHANGE: the MANAGER feedback that approved THIS self sheet, when the viewer
   * may see it. Its rows carry the reviewer's own comment and rating per goal,
   * which are rendered beside the employee's so a second admin or manager can
   * compare the two without opening another record.
   *
   * NOTE: null whenever `approvalFeedbackId` is null — the contract hides it
   * from viewers the approver has not shared with, so the comparison simply
   * does not appear rather than showing a gap.
   */
  reviewerFeedback?: Feedback | null;
  /** Rows start expanded on a single-record screen, collapsed in a long list. */
  defaultExpanded?: boolean;
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  feedback,
  onEdit,
  onDelete,
  onApprove,
  onOpenLinked,
  onToggleShare,
  sharing = false,
  reviewerFeedback = null,
  defaultExpanded = false,
}) => {
  const hasRows = feedback.items.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded && hasRows);

  const isSelf = feedback.kind === "SELF";
  const isApproval = feedback.selfFeedbackId !== null;
  // CHANGE: manager feedback is private until shared, so every MANAGER card
  // states which it is. `visibleToEmployee` is null on SELF, where it means
  // nothing.
  const isShared = feedback.visibleToEmployee === true;

  /**
   * The reviewer's row for a given goal, matched on `objectiveId` — an
   * objective may be used only once per sheet, so it is a stable key, while row
   * ids change on every save and cannot be relied on.
   */
  const reviewerRowFor = (objectiveId: number) =>
    reviewerFeedback?.items.find((row) => row.objectiveId === objectiveId) ?? null;

  const hasComparison = Boolean(reviewerFeedback && reviewerFeedback.items.length > 0);
  const canApprove = Boolean(onApprove) && isSelf && feedback.status === "SUBMITTED";

  return (
    <article className="rounded-xl border border-gray-200 bg-white transition-colors dark:border-gray-700 dark:bg-gray-800">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-3.5 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {feedback.period}
            </span>
            {/* Muted and smaller, so the period stays the heading and this
                reads as the explanation of it. */}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {getQuarterMonths(feedback.quarter)}
            </span>
            {isSelf ? (
              <FeedbackStatusBadge status={feedback.status} />
            ) : (
              <>
                <FeedbackKindTag isApproval={isApproval} period={feedback.period} />
                {/* CHANGE: shown only to whoever can CHANGE the visibility.
                    `onToggleShare` is passed just to the author or an admin
                    (EmployeeFeedbackScreen gates it on kind MANAGER + editable),
                    so this is exactly the audience the tag is for. An employee
                    reading feedback addressed to them learned nothing from
                    "Shared with employee" — they are the employee, and they are
                    reading it. */}
                {onToggleShare && <FeedbackVisibilityTag shared={isShared} />}
              </>
            )}
          </div>

          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {isSelf ? "Written by " : "From "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {feedback.authorName}
            </span>
            {" · "}
            {formatFeedbackDateTime(feedback.submittedAt ?? feedback.createdAt)}
          </p>

          {/* An approved sheet names its reviewer and links to their comment. */}
          {isSelf && feedback.status === "APPROVED" && (
            <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
              Approved by{" "}
              <span className="font-medium">{feedback.approvedByName ?? "—"}</span> on{" "}
              {formatFeedbackDateTime(feedback.approvedAt)}
              {feedback.approvalFeedbackId !== null && onOpenLinked && (
                <button
                  type="button"
                  onClick={() => onOpenLinked(feedback.approvalFeedbackId as number)}
                  className="ml-2 font-medium text-primary underline-offset-2 hover:underline"
                >
                  View their comment
                </button>
              )}
            </p>
          )}

          {/* An approving record links back to the sheet it approved. */}
          {isApproval && feedback.selfFeedbackId !== null && onOpenLinked && (
            <button
              type="button"
              onClick={() => onOpenLinked(feedback.selfFeedbackId as number)}
              className="mt-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
            >
              View the goal sheet this approved
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <RatingDisplay value={feedback.overallRating} />

          {/* CHANGE: share / hide. One PUT with the full body and the new flag;
              the label says what the click DOES, not what the state is, so it
              can't be misread as a status badge (that is the tag above). */}
          {onToggleShare && !isSelf && (
            <HeaderButtons
              type="button"
              variant={isShared ? "tertiary" : "secondary"}
              size="small"
              loading={sharing}
              disabled={sharing}
              onClick={() => onToggleShare(feedback, !isShared)}
            >
              {isShared ? "Make private" : "Share with employee"}
            </HeaderButtons>
          )}

          {canApprove && (
            <HeaderButtons
              type="button"
              variant="primary"
              size="small"
              onClick={() => onApprove?.(feedback)}
            >
              Approve
            </HeaderButtons>
          )}
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(feedback)}
              title="Edit"
              aria-label="Edit feedback"
              className="btn-primary cursor-pointer rounded-lg px-2 py-2"
            >
              <FaEdit size={12} />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(feedback)}
              title="Delete"
              aria-label="Delete feedback"
              className="btn-red cursor-pointer rounded-lg px-2 py-2"
            >
              <FaRegTrashAlt size={12} />
            </button>
          )}
        </div>
      </div>

      {/* ── Comment ─────────────────────────────────────────────────────── */}
      {feedback.overallComment && (
        <p className="whitespace-pre-wrap border-t border-gray-100 px-3.5 py-2.5 text-sm leading-relaxed text-gray-700 dark:border-gray-700 dark:text-gray-200">
          {feedback.overallComment}
        </p>
      )}

      {/* ── Goal rows ───────────────────────────────────────────────────── */}
      {hasRows && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setExpanded((previous) => !previous)}
            aria-expanded={expanded}
            className="flex w-full items-center gap-2 px-3.5 py-2 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {expanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
            {feedback.items.length} goal{feedback.items.length === 1 ? "" : "s"}
            {feedback.totalWeightage > 0 && (
              <span className="text-gray-400 dark:text-gray-500">
                · {feedback.totalWeightage}% weightage
              </span>
            )}
            {reviewerFeedback && (
              <span className="text-blue-600 dark:text-blue-400">
                · {hasComparison ? "reviewer scores" : "reviewer comment"}
              </span>
            )}
          </button>

          {/* CHANGE: a checklist, not a table. Each goal reads top-down —
              area + weightage, then the objective, then the KPI / achievement —
              so the longest field is no longer squeezed into the narrowest
              column, and nothing scrolls sideways on a laptop or a phone. */}
          {/* CHANGE: when a reviewer has acted on this sheet, their details sit
              in a column to the RIGHT of the goals, so a second admin or
              manager can read the employee's goals and the approver's verdict
              together instead of opening the approval separately. */}
          {expanded && (
            <div
              className={`px-3.5 pb-3 ${
                reviewerFeedback ? "grid gap-3 lg:grid-cols-[minmax(0,1fr)_280px]" : ""
              }`}
            >
              <ol className="flex min-w-0 flex-col gap-2">
                {feedback.items.map((item, index) => {
                  const review = reviewerRowFor(item.objectiveId);

                  return (
                    <li
                      key={item.id}
                      className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700"
                    >
                      {/* CHANGE: rating moved up here, onto the area row with
                          the weightage. It belongs with the other two numbers,
                          not stranded under the prose where "No rating" read as
                          part of the achievement. */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold tabular-nums text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                            {index + 1}
                          </span>
                          <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                            {item.areaName}
                          </span>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <RatingDisplay value={item.rating} compact />
                          {item.weightage !== null && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {item.weightage}%
                            </span>
                          )}
                        </div>
                      </div>

                      <p className="mt-1 pl-7 text-sm text-gray-700 dark:text-gray-300">
                        {item.objectiveName}
                      </p>

                      <p className="mt-1 whitespace-pre-wrap pl-7 text-sm text-gray-600 dark:text-gray-300">
                        {item.achievement || "—"}
                      </p>

                      {/* The reviewer's note for THIS goal, when they scored it. */}
                      {review && reviewerFeedback && (
                        <div className="mt-1.5 ml-7 rounded-md border-l-2 border-blue-400 bg-blue-50/70 px-2 py-1 dark:bg-blue-900/20">
                          <div className="flex items-center justify-between gap-2">
                            <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                              {reviewerFeedback.authorName}
                            </p>
                            <RatingDisplay value={review.rating} compact />
                          </div>
                          <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">
                            {review.achievement || "—"}
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>

              {reviewerFeedback && (
                <aside className="h-fit rounded-lg border border-blue-200 bg-blue-50/60 p-2.5 dark:border-blue-800 dark:bg-blue-900/20">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                    Approved by {reviewerFeedback.authorName}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {formatFeedbackDateTime(
                      reviewerFeedback.submittedAt ?? reviewerFeedback.createdAt,
                    )}
                  </p>
                  <div className="mt-1.5">
                    <RatingDisplay value={reviewerFeedback.overallRating} />
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                    {reviewerFeedback.overallComment || "No comment left."}
                  </p>
                </aside>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default FeedbackCard;
